import { useMemo, useState } from 'react';
import useGetFleets from '../../../API/hooks/useGetFleets';
import { FleetListItem } from '../../../API/types/Fleets';
import { RecentFleet, RECENT_FLEETS_LIMIT } from '../../../utils/fleetStorage';
import Icon from '../../common/Icon/Icon';
import { IconKeys } from '../../common/Icon/IconKeys';
import Input from '../../common/Input/Input';
import S from './ChangeFleetModalStyles';

type Props = {
  activeFleetId: string;
  recentFleets: RecentFleet[];
  onSelect: (fleet: RecentFleet) => void;
};

const matchesSearch = (phrase: string, name: string, organizationName?: string): boolean => !phrase
  || name.toLowerCase().includes(phrase)
  || (organizationName || '').toLowerCase().includes(phrase);

function ChangeFleetModal({ activeFleetId, recentFleets, onSelect }: Props): JSX.Element {
  const [search, setSearch] = useState('');
  const { data: fleets, isLoading, isError } = useGetFleets(true);

  const phrase = search.trim().toLowerCase();

  const allFleets = useMemo(() => (fleets || [])
    .filter((fleet: FleetListItem) => !!fleet?._id && !!fleet?.name)
    .filter((fleet: FleetListItem) => matchesSearch(phrase, fleet.name, fleet.organizationName))
    .sort((a: FleetListItem, b: FleetListItem) => a.name.localeCompare(b.name)), [fleets, phrase]);

  // recent fleets come from a cookie, so their names can be outdated
  const recentToRender = useMemo(() => {
    const recent = recentFleets.slice(0, RECENT_FLEETS_LIMIT).map((item) => {
      const fleet = fleets?.find((listItem: FleetListItem) => listItem._id === item.id);
      return {
        _id: item.id,
        name: fleet?.name || item.name,
        organizationName: fleet?.organizationName,
      };
    });

    return recent.filter((fleet) => matchesSearch(phrase, fleet.name, fleet.organizationName));
  }, [recentFleets, fleets, phrase]);

  const renderFleet = (fleet: FleetListItem): JSX.Element => (
    <S.FleetItem
      key={fleet._id}
      type="button"
      className={fleet._id === activeFleetId ? 'active' : ''}
      onClick={() => onSelect({ id: fleet._id, name: fleet.name })}
    >
      <Icon iconKey={IconKeys.SUITCASE} />
      <span className="name" title={fleet.name}>{fleet.name}</span>
      {!!fleet.organizationName && (
        <span className="organization" title={fleet.organizationName}>{fleet.organizationName}</span>
      )}
      {fleet._id === activeFleetId && <span className="badge">Active</span>}
    </S.FleetItem>
  );

  return (
    <S.Wrapper>
      <Input
        name="fleetSearch"
        placeholder="Search fleet"
        value={search}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
      />
      {!!recentToRender.length && (
        <S.Section>
          <p className="section-title">Recently opened</p>
          <div className="fleets">
            {recentToRender.map(renderFleet)}
          </div>
        </S.Section>
      )}
      <S.Section>
        <p className="section-title">All fleets</p>
        <div className="fleets scrollable">
          {isLoading && <p className="message">Loading...</p>}
          {isError && <p className="message error">Fleets list is not available right now.</p>}
          {!isLoading && !isError && !allFleets.length && <p className="message">No fleets found</p>}
          {allFleets.map(renderFleet)}
        </div>
      </S.Section>
    </S.Wrapper>
  );
}

export default ChangeFleetModal;
